import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Download, Copy, Check, Mail, FileText, Image as ImageIcon } from 'lucide-react';
import TopNav from '../components/sections/TopNav';
import SiteFooter from '../components/sections/SiteFooter';
import SubmitTipButton from '../components/SubmitTipButton';
import { useJsonLd, ORG_JSONLD, buildBreadcrumb } from '../utils/jsonLd';
import PageSeoTag from '../components/PageSeoTag';
import { LOGO_WIDE } from '../utils/brand';

const LOGO_URL = LOGO_WIDE;

const ASSETS = [
  {
    id: 'logo-primary',
    label: 'Primary Logo (320×320 PNG, transparent)',
    href: LOGO_URL,
    filename: 'outlaw-media-logo.png',
    preview: LOGO_URL,
    bg: 'bg-zinc-950',
  },
  {
    id: 'logo-light',
    label: 'Logo on Black (PNG)',
    href: LOGO_URL,
    filename: 'outlaw-media-logo-dark.png',
    preview: LOGO_URL,
    bg: 'bg-black',
  },
  {
    id: 'logo-on-red',
    label: 'Logo on Red (PNG)',
    href: LOGO_URL,
    filename: 'outlaw-media-logo-red.png',
    preview: LOGO_URL,
    bg: 'bg-red-700',
  },
];

const BIO_SHORT =
  "Outlaw Media is an independent crime news outlet covering Australia's underworld - gangland conflicts, organised crime, and investigations the mainstream media won't touch. Founded by Melbourne-based journalist Ryan Naumenko, the outlet publishes on outlawmedia.com.au, Instagram, and YouTube.";

const BIO_ONELINE =
  "Outlaw Media - independent crime journalism from inside Australia's underworld.";

const CopyBtn = ({ text, testId }) => {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={async () => {
        try { await navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1800); }
        catch { /* ignore */ }
      }}
      className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded text-[10px] font-black uppercase tracking-widest bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 hover:border-red-500/50 text-gray-200 hover:text-white transition-colors"
    >
      {copied ? <Check className="h-3 w-3 text-emerald-300" /> : <Copy className="h-3 w-3" />}
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
};

const PressKit = () => {
  useJsonLd(ORG_JSONLD, 'press-org');
  useJsonLd(buildBreadcrumb([
    { name: 'Home', url: 'https://www.outlawmedia.com.au/' },
    { name: 'Press Kit', url: 'https://www.outlawmedia.com.au/press' },
  ]), 'press-breadcrumb');

  return (
    <div className="min-h-screen bg-black text-white" data-testid="press-page">
      <TopNav />
      <PageSeoTag pageKey="press" />

      <header className="relative pt-8 pb-8 px-4 border-b border-zinc-800">
        <div className="max-w-3xl mx-auto">
          <Link
            to="/"
            data-testid="press-back-link"
            className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-gray-400 hover:text-red-400 transition-colors mb-4"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back to Home
          </Link>
          <p className="text-[11px] font-bold uppercase tracking-wider text-red-400 mb-1">
            Press Kit
          </p>
          <h1 className="text-3xl md:text-4xl font-black tracking-tight leading-tight">
            For Journalists, <span className="text-red-500">Reporters &amp; Outlets</span>
          </h1>
          <p className="mt-3 text-sm text-gray-400 max-w-2xl">
            Everything you need to cite, reference, or syndicate Outlaw Media - logos, boilerplate,
            and a direct contact.
          </p>
        </div>
      </header>

      <main className="px-4 py-8">
        <div className="max-w-3xl mx-auto space-y-10">

          {/* Logos */}
          <section data-testid="press-logos">
            <div className="flex items-center gap-2 mb-4">
              <ImageIcon className="h-5 w-5 text-red-500" />
              <h2 className="text-xl sm:text-2xl font-black uppercase tracking-wider">Logos</h2>
            </div>
            <div className="grid sm:grid-cols-3 gap-3">
              {ASSETS.map((a) => (
                <div
                  key={a.id}
                  data-testid={`press-asset-${a.id}`}
                  className="rounded-xl border border-zinc-800 bg-zinc-900/60 overflow-hidden"
                >
                  <div className={`aspect-square flex items-center justify-center p-6 ${a.bg}`}>
                    <img src={a.preview} alt={a.label} className="max-w-full max-h-full object-contain" loading="lazy" />
                  </div>
                  <div className="p-3">
                    <p className="text-[11px] text-gray-400 mb-2 leading-snug">{a.label}</p>
                    <a
                      href={a.href}
                      download={a.filename}
                      data-testid={`press-asset-download-${a.id}`}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded text-[10px] font-black uppercase tracking-widest bg-red-600 hover:bg-red-500 text-white transition-colors"
                    >
                      <Download className="h-3 w-3" /> Download
                    </a>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-[10px] text-gray-500 uppercase tracking-widest mt-3">
              Don't recolour, distort, or place a competing logo within 20px of ours.
            </p>
          </section>

          {/* Boilerplate */}
          <section data-testid="press-boilerplate">
            <div className="flex items-center gap-2 mb-4">
              <FileText className="h-5 w-5 text-red-500" />
              <h2 className="text-xl sm:text-2xl font-black uppercase tracking-wider">Boilerplate Bios</h2>
            </div>

            <div className="rounded-xl border border-zinc-800 bg-zinc-900/60 p-4 sm:p-5 mb-3">
              <div className="flex items-start justify-between gap-3 mb-2">
                <p className="text-[10px] font-black tracking-widest uppercase text-red-400">One-liner</p>
                <CopyBtn text={BIO_ONELINE} testId="press-copy-oneline" />
              </div>
              <p className="text-sm text-gray-200 italic leading-relaxed">"{BIO_ONELINE}"</p>
            </div>

            <div className="rounded-xl border border-zinc-800 bg-zinc-900/60 p-4 sm:p-5">
              <div className="flex items-start justify-between gap-3 mb-2">
                <p className="text-[10px] font-black tracking-widest uppercase text-red-400">Short bio (≈50 words)</p>
                <CopyBtn text={BIO_SHORT} testId="press-copy-short" />
              </div>
              <p className="text-sm text-gray-200 leading-relaxed">{BIO_SHORT}</p>
            </div>
          </section>

          {/* Quick facts */}
          <section data-testid="press-facts">
            <div className="flex items-center gap-2 mb-4">
              <FileText className="h-5 w-5 text-red-500" />
              <h2 className="text-xl sm:text-2xl font-black uppercase tracking-wider">Quick Facts</h2>
            </div>
            <dl className="rounded-xl border border-zinc-800 bg-zinc-900/60 divide-y divide-zinc-800/80">
              {[
                ['Outlet name', 'Outlaw Media'],
                ['Founded', '2024'],
                ['Headquarters', 'Dubai (operational); reporting from Melbourne, Australia'],
                ['Founder & editor', 'Ryan Naumenko'],
                ['Channels', 'outlawmedia.com.au, Instagram (@ryannaumenko), YouTube'],
                ['Coverage focus', "Organised crime, gangland conflict, corruption - Australia"],
              ].map(([k, v]) => (
                <div key={k} className="flex items-start gap-3 px-4 py-2.5 text-sm">
                  <dt className="w-32 sm:w-44 shrink-0 text-[10px] font-black uppercase tracking-widest text-gray-400 pt-1">{k}</dt>
                  <dd className="text-gray-200">{v}</dd>
                </div>
              ))}
            </dl>
          </section>

          {/* Direct contact */}
          <section data-testid="press-contact">
            <div className="flex items-center gap-2 mb-4">
              <Mail className="h-5 w-5 text-red-500" />
              <h2 className="text-xl sm:text-2xl font-black uppercase tracking-wider">Direct Contact</h2>
            </div>
            <div className="rounded-xl border border-red-500/40 bg-red-950/20 p-4 sm:p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <div>
                <p className="text-[10px] font-black uppercase tracking-widest text-red-400 mb-1">Press · Syndication · Legal</p>
                <a
                  href="mailto:ryan@outlawmedia.com.au"
                  data-testid="press-contact-email"
                  className="text-base sm:text-lg font-black text-white hover:text-red-300 transition-colors"
                >
                  ryan@outlawmedia.com.au
                </a>
              </div>
              <CopyBtn text="ryan@outlawmedia.com.au" testId="press-copy-email" />
            </div>
          </section>

          <p className="text-[10px] text-gray-600 tracking-widest uppercase text-center pt-4">
            Last updated · {new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })}
          </p>
        </div>
      </main>

      <SiteFooter />
      <SubmitTipButton />
    </div>
  );
};

export default PressKit;
